import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { mdiCashPlus, mdiCashMultiple, mdiPencil, mdiBriefcaseAccountOutline, mdiCalendar, mdiFormatHorizontalAlignLeft, mdiFormatHorizontalAlignRight } from '@mdi/js';
import { useRouteMatch, Link } from 'react-router-dom';
import { Button, Row, Col, InputGroup, InputGroupAddon, Input, Form } from 'reactstrap';
import Icon from '@mdi/react';
import * as Datetime from 'react-datetime';
import { loadPayments } from '../../../redux/modules/payments';
import { updateValues } from '../../../redux/modules/filters';
import StyledTable from '../../../components/StyledTable/StyledTable';
import FullPageLoader from '../../../components/FullPageLoader/FullPageLoader';
import PageTitle from '../../../components/PageTitle/PageTitle';
import moment from 'moment';
import _ from 'lodash';

import { toAccountIds } from '../../../components/FiltersForm/FiltersForm';
import '../../../components/FiltersForm/FiltersForm.css';
import '../../../components/DateTimePicker/DateTimePicker.css';

const defaultMonthRange = () => ({
    from: moment().startOf('month').toISOString(),
    to: moment().endOf('month').toISOString(),
});

const singleAccountId = (value, user) => {
    const fromFilter = toAccountIds(value)[0] || '';
    if (fromFilter)
        return fromFilter;
    return (user && !user.isSuperAdmin() ? user.account_id : '') || '';
};

const buildApiFilters = (f) => {
    const o = {};
    if (f.account_id) {
        o.account_id = f.account_id;
    }
    if (f.from) {
        o.from = f.from;
    }
    if (f.to) {
        o.to = f.to;
    }
    if (f.completed === '0' || f.completed === '1') {
        o.completed = f.completed;
    }
    return o;
};

const makeInitialFilters = (values, user) => {
    const month = defaultMonthRange();
    const hasFrom = values && values.from;
    const hasTo = values && values.to;

    return {
        account_id: singleAccountId(values && values.account_id, user),
        from: hasFrom ? values.from : month.from,
        to: hasTo ? values.to : month.to,
        completed: (values && values.payments_list_completed) || '',
    };
};

const paymentFiltersFromValues = (values, user) => ({
    account_id: singleAccountId(values && values.account_id, user),
    from: (values && values.from) || '',
    to: (values && values.to) || '',
    completed: (values && values.payments_list_completed) || '',
});

const PaymentsList = () => {
    const dispatch                = useDispatch();
    const { path }                = useRouteMatch();
    const { t }                   = useTranslation();
    const { isLoading, payments } = useSelector(state => state.payments);
    const { user }                = useSelector(state => state.auth);
    const { values }              = useSelector(state => state.filters);
    const { accounts }            = useSelector(state => state.accounts);

    const [filters, setFilters]   = useState(() => makeInitialFilters(values, user));

    useEffect(() => {
        if (!values?.from && !values?.to) {
            const month = defaultMonthRange();
            dispatch(updateValues({ ...values, from: month.from, to: month.to }));
            setFilters(f => ({ ...f, from: month.from, to: month.to }));
        }
    // Align store with default month when empty (same idea as FiltersForm).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        dispatch(loadPayments(buildApiFilters(filters)));
    // Initial list only; further loads come from Valider / account change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const filtersDirty = !_.isEqual(
        {
            account_id: filters.account_id || '',
            from: filters.from || '',
            to: filters.to || '',
            completed: filters.completed || '',
        },
        paymentFiltersFromValues(values, user)
    );

    const onAccountChange = (e) => {
        const account_id = e.target.value;
        const next = { ...filters, account_id };
        setFilters(next);
        dispatch(updateValues({ ...values, account_id }));
        dispatch(loadPayments(buildApiFilters(next)));
    };

    const applyFilters = (e) => {
        e.preventDefault();
        if (!filtersDirty) {
            return;
        }
        dispatch(updateValues({
            ...values,
            account_id: filters.account_id,
            from: filters.from,
            to: filters.to,
            payments_list_completed: filters.completed,
        }));
        dispatch(loadPayments(buildApiFilters(filters)));
    };

    let accountOptions = accounts || [];
    if (user && !user.isSuperAdmin()) {
        accountOptions = (accounts || []).filter(a => a.id === user.account_id);
    }

    if (payments === undefined || isLoading) {
        return <FullPageLoader />
    }

    const actionFormatter = (id) =>
        <Button tag={ Link } to={ `${path}/edit/${id}` } color="success"><Icon path={ mdiPencil } size={ 1 } color="white" /></Button>;

    const cellFormatter = (text, smallText, capitalizeSmallText) =>
        <>
            <div>
                { text }
            </div>
            {smallText &&
                <small className={capitalizeSmallText ? 'capitalize': ''}>{ smallText }</small>
            }
        </>;

    const columns = [{
        dataField: 'payment_date',
        text: t('payments.payment_date'),
        sort: true,
        classes: () => 'with-left-icon',
        formatter: (payment_date, row) => cellFormatter(
            moment(payment_date).format('LL'),
            row.value_date ? moment(row.value_date).format('LL') : ''
        ),
    }, {
        dataField: 'account.name',
        text: t('payments.account_id'),
        sort: true,
        formatter: (account_name, p) => cellFormatter(
            account_name,
            p.iban,
            false
        ),
    }, {
        dataField: 'currency',
        text: t('payments.currency'),
        sort: true
    }, {
        dataField: 'amount',
        text: t('payments.amount'),
        sort: true
    }, {
        dataField: 'completed',
        text: t('payments.completed'),
        sort: true,
        formatter: (completed) => completed ? t('payments.ok') : t('payments.not_ok') 
    }, {
        dataField: 'id',
        text: t('common.actions'),
        sort: false,
        formatter: actionFormatter,
        csvExport: false
    }, {
        dataField: 'iban',
        text: t('payments.iban'),
        hidden: true
    }, {
        dataField: 'balance_before',
        text: t('payments.balance_before'),
        hidden: true
    }, {
        dataField: 'value_date',
        text: t('payments.value_date'),
        hidden: true
    }, {
        dataField: 'total_transaction',
        text: t('payments.total_transaction'),
        hidden: true
    }, {
        dataField: 'balance_after',
        text: t('payments.balance_after'),
        hidden: true
    }];

    const defaultSorted = [{
        dataField: 'payment_date',
        order: 'desc'
    }];

    const actions = <Link to={`${path}/new`}>
        <Button color="info">
            <Icon path={ mdiCashPlus } size={ 1 } color="white" /> { t('payments.add') }
        </Button>
    </Link>;

    const fromMoment = filters.from ? moment(filters.from) : moment().startOf('month');
    const toMoment = filters.to ? moment(filters.to) : moment().endOf('month');

    return (
        <div className="PaymentsList">
            <PageTitle
                title={ t('payments.list_title') }
                icon={ <Icon path={ mdiCashMultiple } color="white" /> }
                actions={ actions }
            />
            <Form className="FiltersForm noPrint" onSubmit={ applyFilters }>
                <Row>
                    <Col sm={ 6 } md={ 4 } lg={ 3 }>
                        <InputGroup>
                            <InputGroupAddon addonType="prepend">
                                <span className="input-group-text">
                                    <Icon path={ mdiBriefcaseAccountOutline } size={ 1 } color="grey" />
                                </span>
                            </InputGroupAddon>
                            <Input
                                type="select"
                                className="custom-select"
                                onChange={ onAccountChange }
                                value={ filters.account_id || '' }
                                disabled={ user && !user.isSuperAdmin() }
                            >
                                { user && user.isSuperAdmin() && <option key="0" value="">---</option> }
                                { accountOptions.map((a) =>
                                    <option key={ a.id } value={ a.id }>{ a.name }</option>
                                ) }
                            </Input>
                        </InputGroup>
                    </Col>
                    <Col sm={ 6 } md={ 4 } lg={ 3 }>
                        <InputGroup>
                            <InputGroupAddon addonType="prepend">
                                <span className="input-group-text">
                                    <Icon path={ mdiCalendar } size={ 1 } color="grey" />
                                    <Icon path={ mdiFormatHorizontalAlignLeft } size={ 1 } color="grey" />
                                </span>
                            </InputGroupAddon>
                            <div className="DateTimePicker">
                                <Datetime
                                    value={ fromMoment }
                                    onChange={ (date) => setFilters(f => ({
                                        ...f,
                                        from: date ? moment(date).toISOString() : '',
                                    })) }
                                    timeFormat
                                />
                            </div>
                        </InputGroup>
                    </Col>
                    <Col sm={ 6 } md={ 4 } lg={ 3 }>
                        <InputGroup>
                            <InputGroupAddon addonType="prepend">
                                <span className="input-group-text">
                                    <Icon path={ mdiFormatHorizontalAlignRight } size={ 1 } color="grey" />
                                    <Icon path={ mdiCalendar } size={ 1 } color="grey" />
                                </span>
                            </InputGroupAddon>
                            <div className="DateTimePicker">
                                <Datetime
                                    value={ toMoment }
                                    onChange={ (date) => setFilters(f => ({
                                        ...f,
                                        to: date ? moment(date).endOf('day').toISOString() : '',
                                    })) }
                                    timeFormat
                                />
                            </div>
                        </InputGroup>
                    </Col>
                    <Col sm={ 6 } md={ 4 } lg={ 3 }>
                        <InputGroup>
                            <InputGroupAddon addonType="prepend">
                                <span className="input-group-text">
                                    <Icon path={ mdiCashMultiple } size={ 1 } color="grey" />
                                </span>
                            </InputGroupAddon>
                            <Input
                                type="select"
                                className="custom-select"
                                onChange={ (e) => {
                                    const completed = e.target.value;
                                    setFilters(f => ({ ...f, completed }));
                                } }
                                value={ filters.completed }
                            >
                                <option key="all" value="">---</option>
                                <option value="1">{ t('payments.filter_status_completed') }</option>
                                <option value="0">{ t('payments.filter_status_pending') }</option>
                            </Input>
                        </InputGroup>
                    </Col>
                </Row>
                { filtersDirty && (
                    <Row className="btn-row">
                        <Col sm="12" md={{ size: 4, offset: 4 }}>
                            <Button type="submit" block color="info">{ t('common.validate') }</Button>
                        </Col>
                    </Row>
                ) }
            </Form>
            <StyledTable
                bootstrap4
                keyField="id"
                data={ payments }
                columns={ columns }
                defaultSorted={ defaultSorted }
                sizePerPage={ 25 }
                hideSearch
            />
        </div>
    );
}

export default PaymentsList;
